import moment from "moment";
import type Stripe from "stripe";
import { AmplitudeCategories, createTrackerForCategory } from "./amplitude";
import { WorkspacePlanTier } from "common/interfaces";

const trackEvent = createTrackerForCategory(AmplitudeCategories.ModifyWorkspacePlan);

export type SupportedBillingInterval = Extract<Stripe.Price.Recurring.Interval, "month" | "year">;

enum events {
    ChangeBillingInterval = "ChangeBillingInterval",
    CancelFlowStart = "CancelFlowStart",
    CancelFlowComplete = "CancelFlowComplete",
    DowngradeFlowStart = "DowngradeFlowStart",
    DowngradeFlowComplete = "DowngradeFlowComplete",
    DowngradeRequestWithdraw = "DowngradeRequestWithdraw",
    EnterpriseInquiry = "EnterpriseInquiry",
    ManagePlanDialogOpen = "ManagePlanDialogOpen",
    ReactivateFlowStart = "ReactivateFlowStart",
    ReactivateFlowComplete = "ReactivateFlowComplete",
    UpgradeFlowStart = "UpgradeFlowStart",
    UpgradeFlowComplete = "UpgradeFlowComplete",
    StartSubscriptionEarly = "StartSubscriptionEarly"
}

async function _getBillingController() {
    // @ts-ignore
    return await import("js/controllers/BillingController").then(module => module.default);
}

async function _getWorkspaceController() {
    // @ts-ignore
    return await import("js/controllers/WorkspaceController").then(module => module.default);
}

function _getFullDaysBetween(startTimestamp: number, endTimestamp: number) {
    const startDate = moment(startTimestamp).startOf("day");
    const endDate = moment(endTimestamp).startOf("day");
    return endDate.diff(startDate, "days");
}

export function CancelFlowStart() {
    // Do we need to identify if they are in a trial or not?
    const eventProperties: {} = {};
    trackEvent(events.CancelFlowStart, eventProperties, { audit: true });
}

export function ChangeBillingInterval(props: {
    from_plan_interval: SupportedBillingInterval;
    from_plan_interval_count: 1;
    to_plan_interval: SupportedBillingInterval;
    to_plan_interval_count: 1;
}) {
    trackEvent(events.ChangeBillingInterval, props, { audit: true });
}

export async function CancelFlowComplete() {
    const billingController = await _getBillingController();
    const billing_period_end = billingController.subscription.current_period_end * 1000;
    const days_to_period_end = _getFullDaysBetween(moment().valueOf(), billing_period_end);

    const workspaceController = await _getWorkspaceController();
    const workspace_age_days = _getFullDaysBetween(workspaceController.workspace.createdAt, moment().valueOf());

    const eventProperties = { days_to_period_end, workspace_age_days };
    trackEvent(events.CancelFlowComplete, eventProperties, { audit: true });
}

export function DowngradeFlowStart() {
    const eventProperties : { } = {};
    trackEvent(events.DowngradeFlowStart, eventProperties, { audit: true });
}

export async function DowngradeFlowComplete(props: { downgrade_plan_to: string }) {
    const billingController = await _getBillingController();

    const billing_period_end = billingController.subscription.current_period_end * 1000;
    const days_to_period_end = _getFullDaysBetween(moment().valueOf(), billing_period_end);

    const workspaceController = await _getWorkspaceController();
    const workspace_age_days = _getFullDaysBetween(workspaceController.workspace.createdAt, moment().valueOf());

    const eventProperties = {
        ...props,
        days_to_period_end,
        workspace_age_days
    };
    trackEvent(events.DowngradeFlowComplete, eventProperties, { audit: true });
}

export function DowngradeRequestWithdraw() {
    const eventProperties: {} = {};
    trackEvent(events.DowngradeRequestWithdraw, eventProperties, { audit: true });
}

export function EnterpriseInquiry() {
    const eventProperties: {} = {};
    trackEvent(events.EnterpriseInquiry, eventProperties, { audit: true });
}

export function ManagePlanDialogOpen({ featured_plan, analytics }: { featured_plan?: string, analytics?: { cta: string; } }) {
    const eventProperties = {
        ...(featured_plan && { featured_plan }),
        ...(analytics?.cta && { cta: analytics.cta })
    };
    trackEvent(events.ManagePlanDialogOpen, eventProperties, { audit: true });
}

export function ReactivateFlowStart() {
    const eventProperties: {} = {};
    trackEvent(events.ReactivateFlowStart, eventProperties, { audit: true });
}

export function ReactivateFlowComplete() {
    const eventProperties: {} = {};
    trackEvent(events.ReactivateFlowComplete, eventProperties, { audit: true });
}

export function StartSubscriptionEarly({ days_to_trial_end }) {
    const eventProperties = { days_to_trial_end };

    trackEvent(events.StartSubscriptionEarly, eventProperties, { audit: true });
}

export function UpgradeFlowStart() {
    const eventProperties: {} = {};
    trackEvent(events.UpgradeFlowStart, eventProperties, { audit: true });
}

export async function UpgradeFlowComplete() {
    const workspaceController = await _getWorkspaceController();
    const upgrade_plan_to = workspaceController.plan.name;

    const billingController = await _getBillingController();
    const quantity_seats_total = workspaceController.plan.tier === WorkspacePlanTier.TEAM ? billingController.paidSeatCount : null;

    const eventProperties = {
        upgrade_plan_to,
        ...(quantity_seats_total && { quantity_seats_total })
    };
    trackEvent(events.UpgradeFlowComplete, eventProperties, { audit: true });
}
